import type { LegacyMatch, PlayerSummary } from "../api/legacyTypes";
import { useState } from "react";
import { slotFeederHint } from "../utils/bracketFeeders";
import { entitySlug } from "../utils/slug";
import { CountryMark } from "./CountryMark";
import { EntityLink } from "./EntityLink";
import { EntityLogo } from "./EntityLogo";
import { FeederPlaceholder } from "./FeederPlaceholder";
import { LocalDateTime } from "./LocalDateTime";
import { DotaMatchPreview } from "./DotaMatchPreview";
import { CounterStrikeMatchPreview } from "./CounterStrikeMatchPreview";
import { OsuMatchPreview } from "./OsuMatchPreview";

function row(match: LegacyMatch, gameId: string, slot: 1 | 2 | 3 | 4, allMatches: LegacyMatch[], playerSummaries: Record<string, PlayerSummary>) {
  const name = match[`participant${slot}` as keyof LegacyMatch] as string;
  const score = match[`score${slot}` as keyof LegacyMatch] as string;
  const teamId = slot === 1 ? match.teamId1 : slot === 2 ? match.teamId2 : "";
  const remoteLogo = slot === 1 ? match.logo1 : slot === 2 ? match.logo2 : "";
  const logo = teamId ? `/media/organization/${teamId}` : remoteLogo;
	const country = remoteLogo.startsWith("country:") ? remoteLogo.slice("country:".length) : "";
  const individual = gameId === "trackmania" || ((gameId === "chess" || gameId === "ageofempires" || gameId === "osu") && !teamId);
  const summary = name && individual ? playerSummaries[`${gameId}/${entitySlug(name)}`] : undefined;
  const hint = !name ? slotFeederHint(match, slot, gameId, allMatches) : undefined;
  return (
    <div className="match-row" key={`${slot}:${name}`}>
      <span className="match-row-identity">
        {name ? <>{(summary?.country || country) && <CountryMark country={summary?.country || country} />}<EntityLink gameId={gameId} kind={individual ? "player" : "team"} name={name} entityId={teamId} logoUrl={country ? "" : logo} showLogo={!country} /></> : <FeederPlaceholder gameId={gameId} hint={hint} />}
        {summary?.organization && <a className="match-row-organization" href={`/organizations/${summary.organization.id}`} title={summary.organization.name}><EntityLogo src={summary.organization.logo_url || `/media/organization/${summary.organization.id}`} name={summary.organization.name} /></a>}
      </span>
      {score && <strong>{score}</strong>}
    </div>
  );
}

export function MatchCard({ match, gameId, tournamentId = "", allMatches = [match], playerSummaries = {} }: { match: LegacyMatch; gameId: string; tournamentId?: string; allMatches?: LegacyMatch[]; playerSummaries?: Record<string, PlayerSummary> }) {
  const [preview, setPreview] = useState(false);
  const slots = gameId === "trackmania" ? ([1, 2, 3, 4] as const) : ([1, 2] as const);
  const stageLabel = [match.group, match.round].filter((value, index, values) => value && values.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index).join(" · ") || match.bracket;
  return (
    <article className="match-card">
      <div className="match-card-meta">
        <span>{stageLabel}</span>
        <span className={`status status-${match.status}`}>{match.status}</span>
      </div>
      {slots.map((slot) => row(match, gameId, slot, allMatches, playerSummaries))}
      <div className="match-card-footer"><LocalDateTime value={match.startsAt} status={match.status} />{(gameId === "dota2" || gameId === "counterstrike" || gameId === "osu") && tournamentId && <button className="match-stats-trigger" type="button" onClick={() => setPreview(true)}>{gameId === "osu" ? "Map scores" : "Quick stats"}</button>}</div>
      {preview && gameId === "dota2" && <DotaMatchPreview match={match} tournamentId={tournamentId} onClose={() => setPreview(false)} />}
      {preview && gameId === "counterstrike" && <CounterStrikeMatchPreview match={match} tournamentId={tournamentId} onClose={() => setPreview(false)} />}
      {preview && gameId === "osu" && <OsuMatchPreview match={match} onClose={() => setPreview(false)} />}
    </article>
  );
}
